import React, { useState } from 'react';
import { Tag, message, Button, Form, Input, Divider } from 'antd';
import TabularList from 'components/TabularList/TabularList';
import { Add } from '@carbon/icons-react';
import dayjs from 'dayjs';
import { DATE_FORMAT_US_SLASH } from 'components/AriaDatePicker/AriaDatePicker';
import providerService from 'services/providers.service';
import Card, { CardDelete, CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import { ENDASH } from 'constants';
import { DatePicker } from 'components/ui/DatePicker';
import Switch from 'components/Switch/Switch';

const ResidentCard = ({ resident, idx, isEditing, form, onDelete, type }) => {
  const isAdult = type === 'adult';

  return (
    <Card
      key={resident?.id || `new-${idx}`}
      className="mb-4"
      collapsible
      title={resident?.id ? resident?.person?.displayName || ENDASH : 'Unsaved'}
      subtitle={resident?.id && isAdult ? resident?.relationship : undefined}
      extra={isEditing ? <CardDelete onConfirm={() => onDelete(idx)} /> : null}
      noBodyPadding
    >
      {/* Hidden fields for IDs */}
      {isEditing && (
        <>
          <Form.Item name={[type, idx, 'id']} hidden>
            <Input />
          </Form.Item>
          <Form.Item name={[type, idx, 'person', 'id']} hidden>
            <Input />
          </Form.Item>
        </>
      )}
      <TabularList
        indented
        data={[
          {
            title: 'First name',
            value: isEditing ? (
              <Form.Item
                name={[type, idx, 'person', 'firstName']}
                className="mb-0"
                rules={[{ required: true, message: 'First name is required' }]}
              >
                <Input placeholder="Enter first name" className="max-w-72" />
              </Form.Item>
            ) : (
              resident.person.firstName
            ),
            titleRequired: true,
          },
          {
            title: 'Last name',
            value: isEditing ? (
              <Form.Item
                name={[type, idx, 'person', 'lastName']}
                className="mb-0"
                rules={[{ required: true, message: 'Last name is required' }]}
              >
                <Input placeholder="Enter last name" className="max-w-72" />
              </Form.Item>
            ) : (
              resident.person.lastName
            ),
            titleRequired: true,
          },
          {
            title: 'Date of birth',
            value: isEditing ? (
              <Form.Item
                name={[type, idx, 'dateOfBirth']}
                className="mb-0"
                rules={[{ required: true, message: 'Date of birth is required' }]}
              >
                <DatePicker className="max-w-72" />
              </Form.Item>
            ) : (
              dayjs(resident.dateOfBirth).format(DATE_FORMAT_US_SLASH)
            ),
            titleRequired: true,
          },
          ...(isAdult
            ? [
                {
                  title: 'Relationship to the provider',
                  value: isEditing ? (
                    <Form.Item
                      name={[type, idx, 'relationship']}
                      className="mb-0"
                      rules={[{ required: true, message: 'Relationship is required' }]}
                    >
                      <Input placeholder="Enter relationship" className="max-w-72" />
                    </Form.Item>
                  ) : (
                    resident.relationship
                  ),
                  titleRequired: true,
                },
              ]
            : []),
        ]}
      />
    </Card>
  );
};

const Residents = ({ application, isEditable = false }) => {
  const [localResidents, setLocalResidents] = useState(application?.residents || []);
  const [localChildResidents, setLocalChildResidents] = useState(application?.childResidents || []);
  const [adultResidentsPresent, setAdultResidentsPresent] = useState(application?.adultResidentsPresent);
  const [childResidentsPresent, setChildResidentsPresent] = useState(application?.childResidentsPresent);
  const [deletedResidents, setDeletedResidents] = useState([]);
  const [deletedChildResidents, setDeletedChildResidents] = useState([]);
  const [form] = Form.useForm();

  const { isEditing, editProps } = useEditCard({
    id: 'residents',
    onEditStart: () =>
      form.setFieldsValue({
        adultResidentsPresent,
        childResidentsPresent,
        adult: localResidents.map((res) => ({
          id: res.id,
          person: { id: res.person.id, firstName: res.person.firstName, lastName: res.person.lastName },
          dateOfBirth: res.dateOfBirth ? dayjs(res.dateOfBirth).format('YYYY-MM-DD') : '',
          relationship: res.relationship,
        })),
        child: localChildResidents.map((res) => ({
          id: res.id,
          person: { id: res.person.id, firstName: res.person.firstName, lastName: res.person.lastName },
          dateOfBirth: res.dateOfBirth ? dayjs(res.dateOfBirth).format('YYYY-MM-DD') : '',
        })),
      }),
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      await providerService.updateApplication(application.id, {
        ...application,
        adultResidentsPresent: values.adultResidentsPresent,
        childResidentsPresent: values.childResidentsPresent,
      });
      if (values.adultResidentsPresent && values.adult?.length > 0) {
        for (const resident of values.adult) {
          await providerService.addUpdateResidents(application.id, {
            ...resident,
            application: { id: application.id },
          });
        }
      } else if (values.adultResidentsPresent === false && localResidents?.length > 0) {
        for (const resident of localResidents) {
          if (resident.id) await providerService.deleteResident(application.id, resident.id);
        }
      }
      if (values.childResidentsPresent && values.child?.length !== 0) {
        for (const resident of values.child) {
          await providerService.addUpdateChildResidents(application.id, {
            ...resident,
            application: { id: application.id },
          });
        }
      } else if (values.childResidentsPresent === false && localChildResidents?.length !== 0) {
        for (const resident of localChildResidents) {
          if (resident.id) await providerService.deleteChildResident(application.id, resident.id);
        }
      }
      for (const resident of deletedResidents) {
        if (resident.id) await providerService.deleteResident(application.id, resident.id);
      }
      for (const resident of deletedChildResidents) {
        if (resident.id) await providerService.deleteChildResident(application.id, resident.id);
      }
      const refreshedApplication = await providerService.getApplication(application.id);
      setLocalResidents(refreshedApplication.residents || []);
      setLocalChildResidents(refreshedApplication.childResidents || []);
      setAdultResidentsPresent(refreshedApplication.adultResidentsPresent);
      setChildResidentsPresent(refreshedApplication.childResidentsPresent);
      setDeletedResidents([]);
      setDeletedChildResidents([]);
      form.resetFields();
      message.success('Residents updated');
    },
    onDiscard: () => {
      setLocalResidents(application?.residents || []);
      setLocalChildResidents(application?.childResidents || []);
      setAdultResidentsPresent(application?.adultResidentsPresent);
      setChildResidentsPresent(application?.childResidentsPresent);
      setDeletedResidents([]);
      setDeletedChildResidents([]);
      form.resetFields();
    },
  });

  const handleDeleteResident = (residentIndex, type) => {
    if (type === 'adult') {
      const resident = localResidents[residentIndex];
      if (resident.id) {
        setDeletedResidents([...deletedResidents, resident]);
      }
      setLocalResidents(localResidents.filter((_, idx) => idx !== residentIndex));
    } else {
      const resident = localChildResidents[residentIndex];
      if (resident.id) {
        setDeletedChildResidents([...deletedChildResidents, resident]);
      }
      setLocalChildResidents(localChildResidents.filter((_, idx) => idx !== residentIndex));
    }
  };

  const handleAddResident = (type) => {
    const newResident = {
      id: null,
      person: {
        id: null,
        firstName: '',
        lastName: '',
      },
      dateOfBirth: new Date().toISOString(),
      ...(type === 'adult' ? { relationship: '' } : {}),
    };

    if (type === 'adult') {
      setLocalResidents([...localResidents, newResident]);
    } else {
      setLocalChildResidents([...localChildResidents, newResident]);
    }
  };

  const adultResidentsPresentWatch = Form.useWatch('adultResidentsPresent', form);
  const childResidentsPresentWatch = Form.useWatch('childResidentsPresent', form);

  return (
    <Card title="Residents" extra={isEditable ? <CardEdit {...editProps} /> : null} noBodyPadding stickyHeader>
      <Form form={form} disabled={editProps.loading}>
        <TabularList
          indented
          data={[
            {
              title: 'Adults over the age of 18 living at the facility?',
              value: isEditing ? (
                <Form.Item name="adultResidentsPresent" className="mb-0">
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              ) : adultResidentsPresent ? (
                'Yes'
              ) : (
                'No'
              ),
              titleRequired: true,
            },
            {
              title: 'Children under the age of 8 living at the facility?',
              value: isEditing ? (
                <Form.Item name="childResidentsPresent" className="mb-0">
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              ) : childResidentsPresent ? (
                'Yes'
              ) : (
                'No'
              ),
              titleRequired: true,
            },
          ]}
        />

        {/* Adult Residents */}
        {(isEditing ? adultResidentsPresentWatch : adultResidentsPresent) && (
          <>
            <Divider className="my-0" />

            <div className="p-7">
              <div className="flex items-center gap-3 mb-4">
                <h4 className="text-base font-semibold">Adult residents</h4>
                <Tag>{localResidents.length}</Tag>
              </div>

              {localResidents.map((resident, idx) => (
                <ResidentCard
                  key={resident?.id || `new-adult-${idx}`}
                  resident={resident}
                  idx={idx}
                  isEditing={isEditing}
                  form={form}
                  onDelete={(i) => handleDeleteResident(i, 'adult')}
                  type="adult"
                />
              ))}

              {isEditing && isEditable && (
                <Button
                  color="default"
                  variant="dashed"
                  onClick={() => handleAddResident('adult')}
                  icon={<Add size={20} />}
                  block
                  className="mt-4"
                  disabled={editProps.loading}
                >
                  Add adult resident
                </Button>
              )}
            </div>
          </>
        )}

        {/* Child Residents */}
        {(isEditing ? childResidentsPresentWatch : childResidentsPresent) && (
          <>
            <Divider className="mt-0 mb-0" />

            <div className="p-7">
              <div className="flex items-center gap-3 mb-4">
                <h4 className="text-base font-semibold">Child residents</h4>
                <Tag>{localChildResidents.length}</Tag>
              </div>

              {localChildResidents.map((resident, idx) => (
                <ResidentCard
                  key={resident?.id || `new-child-${idx}`}
                  resident={resident}
                  idx={idx}
                  isEditing={isEditing}
                  form={form}
                  onDelete={(i) => handleDeleteResident(i, 'child')}
                  type="child"
                />
              ))}

              {isEditing && isEditable && (
                <Button
                  color="default"
                  variant="dashed"
                  onClick={() => handleAddResident('child')}
                  icon={<Add size={20} />}
                  block
                  className="mt-4"
                  disabled={editProps.loading}
                >
                  Add child resident
                </Button>
              )}
            </div>
          </>
        )}
      </Form>
    </Card>
  );
};

export default Residents;
